import React, { useMemo, useState } from 'react';
import { Clock, Loader2, Repeat } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { api } from '../lib/api';
import { toast } from 'sonner';

const WD = [
  { v: 0, l: 'L' }, { v: 1, l: 'M' }, { v: 2, l: 'M' }, { v: 3, l: 'J' },
  { v: 4, l: 'V' }, { v: 5, l: 'S' }, { v: 6, l: 'D' },
];
const WD_FULL = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábados', 'domingos'];
const addDays = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString('en-CA');
};

export const HappyHourRequestDialog = ({ station, open, onOpenChange }) => {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' });
  const [date, setDate] = useState(today);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [discount, setDiscount] = useState('');
  const [discountDiesel, setDiscountDiesel] = useState('');
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [weekdays, setWeekdays] = useState([]);
  const [untilDate, setUntilDate] = useState('');

  const occCount = useMemo(() => {
    if (!repeat || !date || !untilDate || weekdays.length === 0) return 0;
    let n = 0;
    let d = new Date(`${date}T12:00:00`);
    const until = new Date(`${untilDate}T12:00:00`);
    while (d <= until && n < 60) {
      if (weekdays.includes((d.getDay() + 6) % 7)) n += 1;
      d.setDate(d.getDate() + 1);
    }
    return n;
  }, [repeat, date, untilDate, weekdays]);

  const toggleDay = (v) => setWeekdays(w => w.includes(v) ? w.filter(x => x !== v) : [...w, v]);

  const submit = async () => {
    if (!date || !startTime || !endTime) { toast.error('Elige fecha, hora de inicio y de fin'); return; }
    const d = parseFloat(discount);
    if (isNaN(d) || d <= 0) { toast.error('Ingresa el monto a bajar en gasolinas (ej. Q1.00 o Q2.00)'); return; }
    const dd = discountDiesel.trim() === '' ? null : parseFloat(discountDiesel);
    if (dd !== null && (isNaN(dd) || dd <= 0)) { toast.error('El monto de diésel debe ser mayor a 0 (o déjalo vacío para usar el mismo de gasolinas)'); return; }
    if (repeat) {
      if (weekdays.length === 0) { toast.error('Elige al menos un día de la semana'); return; }
      if (!untilDate) { toast.error("Define la fecha 'hasta' de la serie"); return; }
      if (occCount === 0) { toast.error('La serie no genera ninguna fecha — revisa días y rango'); return; }
    }
    setSending(true);
    try {
      await api.post('/happy-hours', {
        station_id: station.id,
        start_at: new Date(`${date}T${startTime}:00`).toISOString(),
        end_at: new Date(`${date}T${endTime}:00`).toISOString(),
        discount: d,
        ...(dd !== null ? { discount_diesel: dd } : {}),
        notes,
        ...(repeat ? { repeat_weekdays: weekdays, repeat_until: untilDate } : {}),
      });
      toast.success(repeat ? `Serie de Happy Hour enviada a Pricing (${occCount} fechas)` : 'Solicitud de Happy Hour enviada a Pricing');
      onOpenChange(false);
      setStartTime(''); setEndTime(''); setDiscount(''); setDiscountDiesel(''); setNotes(''); setRepeat(false); setWeekdays([]); setUntilDate('');
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Error al enviar la solicitud', { duration: 8000 });
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-md bg-[#f8f6f1] max-h-[78vh] overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }} data-testid="hh-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-[16px] sm:text-[20px] leading-snug flex items-start gap-2 pr-5"><Clock size={16} className="shrink-0 mt-0.5"/> <span className="min-w-0 break-words">Happy Hour · {station?.name}</span></DialogTitle>
        </DialogHeader>
        <Button onClick={submit} disabled={sending} className="w-full h-11 rounded-full bg-[#1c1c1c] text-white text-[13px]" data-testid="hh-req-submit">
          {sending ? <Loader2 size={14} className="animate-spin mr-1.5"/> : <Clock size={14} className="mr-1.5"/>} {repeat ? `Solicitar serie de Happy Hour${occCount ? ` (${occCount})` : ''}` : 'Solicitar Happy Hour'}
        </Button>
        <div className="space-y-3.5 pb-6">
          <p className="text-[12px] text-[#5a5a5a]">Define el período (sin límite de duración · puede ser todo el día) y el monto a bajar. Pricing debe aprobar antes de que aplique.</p>
          <label className="flex items-center gap-2 cursor-pointer bg-white border border-[#e8e5de] rounded-lg px-3 py-2" data-testid="hh-req-repeat-toggle">
            <input type="checkbox" checked={repeat} onChange={e => setRepeat(e.target.checked)} className="w-4 h-4"/>
            <Repeat size={13} className="text-[#0f1d6f]"/>
            <span className="text-[12.5px]">Repetir por días de la semana (ej. todos los jueves)</span>
          </label>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#8a8579]">{repeat ? 'Desde' : 'Fecha'}</Label>
            <Input type="date" value={date} min={today} onChange={e => setDate(e.target.value)} className="mt-1 bg-white" data-testid="hh-req-date"/>
          </div>
          {repeat && (
            <>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#8a8579]">Hasta (máx. 1 mes)</Label>
                <Input type="date" value={untilDate} min={date} max={addDays(date, 31)} onChange={e => setUntilDate(e.target.value)} className="mt-1 bg-white" data-testid="hh-req-until"/>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#8a8579]">Días de la semana</Label>
                <div className="flex gap-1.5 mt-1.5" data-testid="hh-req-weekdays">
                  {WD.map((d, i) => (
                    <button
                      key={i} type="button" onClick={() => toggleDay(d.v)}
                      className={`w-9 h-9 rounded-full text-[12.5px] font-medium border transition-colors ${weekdays.includes(d.v) ? 'bg-[#0f1d6f] text-white border-[#0f1d6f]' : 'bg-white text-[#5a5a5a] border-[#d4cfc4]'}`}
                      data-testid={`hh-req-wd-${d.v}`}
                    >{d.l}</button>
                  ))}
                </div>
                {occCount > 0 && (
                  <p className="text-[11.5px] text-[#0f1d6f] mt-1.5" data-testid="hh-req-occ-count">
                    Se programarán <strong>{occCount} fecha(s)</strong>: los {weekdays.sort((a, b) => a - b).map(w => WD_FULL[w]).join(', ')}.
                  </p>
                )}
              </div>
            </>
          )}
          <label className="flex items-center gap-2 cursor-pointer bg-white border border-[#e8e5de] rounded-lg px-3 py-2" data-testid="hh-req-allday">
            <input
              type="checkbox"
              checked={startTime === '00:00' && endTime === '23:59'}
              onChange={e => { if (e.target.checked) { setStartTime('00:00'); setEndTime('23:59'); } else { setStartTime(''); setEndTime(''); } }}
              className="w-4 h-4"
            />
            <span className="text-[12.5px]">Todo el día (00:00 – 23:59)</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#8a8579]">Hora inicio</Label>
              <Input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="mt-1 bg-white" data-testid="hh-req-start"/>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#8a8579]">Hora fin</Label>
              <Input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="mt-1 bg-white" data-testid="hh-req-end"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#8a8579]">Bajar gasolinas (Q)</Label>
              <Input type="number" step="0.25" min="0.05" max="15" inputMode="decimal" placeholder="Ej. 1.00 o 2.00" value={discount}
                onKeyDown={e => { if (['-', 'e', 'E', '+'].includes(e.key)) e.preventDefault(); }}
                onChange={e => setDiscount(e.target.value.replace(/-/g, ''))} className="mt-1 bg-white" data-testid="hh-req-discount"/>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#8a8579]">Bajar diésel (Q)</Label>
              <Input type="number" step="0.25" min="0.05" max="15" inputMode="decimal" placeholder="Igual que gasolinas" value={discountDiesel}
                onKeyDown={e => { if (['-', 'e', 'E', '+'].includes(e.key)) e.preventDefault(); }}
                onChange={e => setDiscountDiesel(e.target.value.replace(/-/g, ''))} className="mt-1 bg-white" data-testid="hh-req-discount-diesel"/>
            </div>
            {repeat && <p className="col-span-2 text-[10.5px] text-[#8a8579] -mt-1">Los precios HH de cada fecha se calcularán al activarse (vigente de ese día − monto).</p>}
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#8a8579]">Nota (opcional)</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} className="mt-1 bg-white min-h-[56px]" data-testid="hh-req-notes"/>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
